import defaultDeliveryPricingData from '../data/deliveryPricing/juizDeFora.json';

const MAX_TEXT_LENGTH = 160;

const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const trimString = (value) => String(value || '').trim();

const normalizeBoolean = (value, fallback) =>
  typeof value === 'boolean' ? value : fallback;

const normalizeNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeText = (value, fallback, maxLength = MAX_TEXT_LENGTH) => {
  const normalized = trimString(value);
  return (normalized || fallback).slice(0, maxLength);
};

const roundCurrency = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const normalizeDeliveryPricingLookupKey = (value) =>
  trimString(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const toNeighborhoodEntries = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (isRecord(value)) {
    return Object.values(value);
  }

  return [];
};

const normalizeAliases = (value) =>
  Array.isArray(value) ? value.map((alias) => trimString(alias)).filter(Boolean) : [];

const normalizeNeighborhoods = (value, rules, fallbackNeighborhoods) => {
  const normalizedNeighborhoods = toNeighborhoodEntries(value)
    .map((entry, index) => {
      const neighborhood = isRecord(entry) ? entry : {};
      const fallback = fallbackNeighborhoods[index] || null;
      const id =
        normalizeText(neighborhood.id, fallback?.id || `bairro-${index + 1}`, 80)
          .toLowerCase()
          .replace(/[^a-z0-9-]+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '') || `bairro-${index + 1}`;
      const name = normalizeText(neighborhood.name, fallback?.name || `Bairro ${index + 1}`);
      const normalizedName =
        normalizeDeliveryPricingLookupKey(neighborhood.normalized_name) ||
        normalizeDeliveryPricingLookupKey(neighborhood.normalizedName) ||
        normalizeDeliveryPricingLookupKey(name) ||
        fallback?.normalizedName ||
        id;
      const distanceKm = Math.max(
        0,
        normalizeNumber(neighborhood.distance_km ?? neighborhood.distanceKm, fallback?.distanceKm || 0),
      );
      const ratePerKmBrl = Math.max(
        0,
        normalizeNumber(
          neighborhood.rate_per_km_brl ?? neighborhood.ratePerKmBrl,
          distanceKm <= rules.thresholdKm
            ? rules.rateUpToThresholdPerKmBrl
            : rules.rateAboveThresholdPerKmBrl,
        ),
      );
      const deliveryPriceBrl = roundCurrency(
        Math.max(
          0,
          normalizeNumber(
            neighborhood.delivery_price_brl ?? neighborhood.deliveryPriceBrl,
            distanceKm * ratePerKmBrl,
          ),
        ),
      );
      const aliases = Array.from(
        new Set(
          [name, normalizedName, id, ...normalizeAliases(neighborhood.aliases)]
            .map((alias) => normalizeText(alias, ''))
            .filter(Boolean),
        ),
      );

      return {
        id,
        name,
        normalizedName,
        zone: normalizeText(neighborhood.zone, fallback?.zone || 'Não informada'),
        distanceKm,
        ratePerKmBrl,
        deliveryPriceBrl,
        aliases,
      };
    })
    .filter((neighborhood) => neighborhood.id && neighborhood.name);

  return normalizedNeighborhoods.length > 0 ? normalizedNeighborhoods : fallbackNeighborhoods;
};

const normalizeLookup = (rawLookup, neighborhoods, fallbackLookup) => {
  const availableIds = new Set(neighborhoods.map((neighborhood) => neighborhood.id));
  const lookup = { ...fallbackLookup };

  if (isRecord(rawLookup)) {
    Object.entries(rawLookup).forEach(([rawKey, rawId]) => {
      const key = normalizeDeliveryPricingLookupKey(rawKey);
      const id = trimString(rawId);

      if (key && availableIds.has(id)) {
        lookup[key] = id;
      }
    });
  }

  neighborhoods.forEach((neighborhood) => {
    [neighborhood.id, neighborhood.name, neighborhood.normalizedName, ...neighborhood.aliases].forEach(
      (candidate) => {
        const key = normalizeDeliveryPricingLookupKey(candidate);

        if (key && !lookup[key]) {
          lookup[key] = neighborhood.id;
        }
      },
    );
  });

  return lookup;
};

const normalizeDeliveryPricingConfigInternal = (value, fallback) => {
  const rawConfig = isRecord(value) ? value : {};
  const fallbackConfig = fallback || {
    enabled: true,
    meta: {
      city: 'Juiz de Fora',
      state: 'MG',
      country: 'BR',
      originAddress: '',
      currency: 'BRL',
      distanceUnit: 'km',
      generatedAt: '',
      pricingNote: '',
      totalNeighborhoods: 0,
    },
    pricingRules: {
      mode: 'threshold_rate',
      thresholdKm: 4,
      rateUpToThresholdPerKmBrl: 2.5,
      rateAboveThresholdPerKmBrl: 3,
      formula: 'delivery_price_brl = distance_km <= 4 ? distance_km * 2.5 : distance_km * 3.0',
    },
    lookup: {},
    neighborhoods: [],
  };
  const rawMeta = isRecord(rawConfig.meta) ? rawConfig.meta : {};
  const rawPricingRules =
    (isRecord(rawConfig.pricing_rules) && rawConfig.pricing_rules) ||
    (isRecord(rawConfig.pricingRules) && rawConfig.pricingRules) ||
    {};
  const pricingRules = {
    mode: normalizeText(rawPricingRules.mode, fallbackConfig.pricingRules.mode, 40),
    thresholdKm: Math.max(
      0,
      normalizeNumber(rawPricingRules.threshold_km ?? rawPricingRules.thresholdKm, fallbackConfig.pricingRules.thresholdKm),
    ),
    rateUpToThresholdPerKmBrl: Math.max(
      0,
      normalizeNumber(
        rawPricingRules.rate_up_to_threshold_per_km_brl ??
          rawPricingRules.rateUpToThresholdPerKmBrl,
        fallbackConfig.pricingRules.rateUpToThresholdPerKmBrl,
      ),
    ),
    rateAboveThresholdPerKmBrl: Math.max(
      0,
      normalizeNumber(
        rawPricingRules.rate_above_threshold_per_km_brl ??
          rawPricingRules.rateAboveThresholdPerKmBrl,
        fallbackConfig.pricingRules.rateAboveThresholdPerKmBrl,
      ),
    ),
    formula: normalizeText(rawPricingRules.formula, fallbackConfig.pricingRules.formula, 260),
  };
  const neighborhoods = normalizeNeighborhoods(
    rawConfig.neighborhoods,
    pricingRules,
    fallbackConfig.neighborhoods,
  );
  const lookup = normalizeLookup(rawConfig.lookup, neighborhoods, fallbackConfig.lookup || {});

  return {
    enabled: normalizeBoolean(rawConfig.enabled, fallbackConfig.enabled),
    meta: {
      city: normalizeText(rawMeta.city, fallbackConfig.meta.city, 80),
      state: normalizeText(rawMeta.state, fallbackConfig.meta.state, 8),
      country: normalizeText(rawMeta.country, fallbackConfig.meta.country, 8),
      originAddress: normalizeText(
        rawMeta.origin_address ?? rawMeta.originAddress,
        fallbackConfig.meta.originAddress,
      ),
      currency: normalizeText(rawMeta.currency, fallbackConfig.meta.currency, 8),
      distanceUnit: normalizeText(
        rawMeta.distance_unit ?? rawMeta.distanceUnit,
        fallbackConfig.meta.distanceUnit,
        12,
      ),
      generatedAt: normalizeText(
        rawMeta.generated_at ?? rawMeta.generatedAt,
        fallbackConfig.meta.generatedAt,
        40,
      ),
      pricingNote: normalizeText(
        rawMeta.pricing_note ?? rawMeta.pricingNote,
        fallbackConfig.meta.pricingNote,
        260,
      ),
      totalNeighborhoods: neighborhoods.length,
    },
    pricingRules,
    lookup,
    neighborhoods,
  };
};

export const DEFAULT_DELIVERY_PRICING_CONFIG = normalizeDeliveryPricingConfigInternal(
  defaultDeliveryPricingData,
  null,
);

export const normalizeDeliveryPricingConfig = (
  value,
  fallback = DEFAULT_DELIVERY_PRICING_CONFIG,
) => normalizeDeliveryPricingConfigInternal(value, fallback);

export const resolveDeliveryNeighborhood = (config, value) => {
  const directId = trimString(
    isRecord(value)
      ? value.id || value.deliveryNeighborhoodId || value.neighborhoodId
      : value,
  );

  if (directId) {
    const byId = config.neighborhoods.find((neighborhood) => neighborhood.id === directId);

    if (byId) {
      return byId;
    }
  }

  const rawName = trimString(
    isRecord(value)
      ? value.name || value.deliveryNeighborhoodName || value.neighborhoodName || value.label
      : value,
  );
  const lookupKey = normalizeDeliveryPricingLookupKey(rawName || directId);

  if (!lookupKey) {
    return null;
  }

  const resolvedId = config.lookup[lookupKey] || '';

  if (resolvedId) {
    return config.neighborhoods.find((neighborhood) => neighborhood.id === resolvedId) || null;
  }

  return (
    config.neighborhoods.find(
      (neighborhood) =>
        neighborhood.normalizedName === lookupKey ||
        neighborhood.aliases.some(
          (alias) => normalizeDeliveryPricingLookupKey(alias) === lookupKey,
        ),
    ) || null
  );
};

export const buildDeliveryPricingSnapshot = (config, value) => {
  const neighborhood = resolveDeliveryNeighborhood(config, value);

  if (!neighborhood) {
    return null;
  }

  return {
    deliveryNeighborhoodId: neighborhood.id,
    deliveryNeighborhoodName: neighborhood.name,
    deliveryNeighborhoodZone: neighborhood.zone,
    deliveryDistanceKm: neighborhood.distanceKm,
    deliveryFee: neighborhood.deliveryPriceBrl,
  };
};

export const getDeliveryNeighborhoodOptions = (config) =>
  [...config.neighborhoods].sort((left, right) => {
    if (left.zone !== right.zone) {
      return left.zone.localeCompare(right.zone, 'pt-BR');
    }

    return left.name.localeCompare(right.name, 'pt-BR');
  });
